import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/ui/SEO';
import { Backpack, Factory, Luggage, Package, Search, ShoppingBag, Tag } from 'lucide-react';

interface Product {
  id: number;
  code: string;
  name: string;
  category: string;
  price: number;
  image_url: string | null;
  stock: number;
  sold_out: number;  // 1 = 재고 없음
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  '내부판매용': Tag,
  '핸드백': ShoppingBag,
  '숄더백': ShoppingBag,
  '크로스백': Backpack,
  '백팩': Backpack,
  '여행용 가방': Luggage,
  'OEM/ODM': Factory,
  '기타': Package,
};

export default function CatalogPage() {
  const [products,  setProducts]  = useState<Product[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('전체');
  const [search,    setSearch]    = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    fetch('/api/catalog')
      .then(r => r.ok ? r.json() : [])
      .then((data: Product[]) => {
        setProducts(data);
        const cats = ['전체', ...Array.from(new Set(data.map(p => p.category)))];
        setCategories(cats);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = products.filter(p => {
    const catOk    = filter === '전체' || p.category === filter;
    const searchOk = p.name.toLowerCase().includes(search.toLowerCase());
    return catOk && searchOk;
  });

  const internalProducts = products.filter(p => p.category === '내부판매용');
  const hasInternal      = internalProducts.length > 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="제품 카탈로그"
        path="/catalog"
        description="COBRAND 전문 제작 가방 카탈로그. 핸드백, 숄더백, 크로스백, 백팩, 여행용 가방. 내부판매용 제품은 실시간 재고 확인 가능."
        keywords="가방카탈로그,핸드백,숄더백,크로스백,백팩,여행가방,COBRAND제품"
        type="website"
      />

      {/* 페이지 헤더 */}
      <div className="bg-white text-stone-900 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-amber-700 text-xs tracking-widest uppercase mb-3">Product Catalog</p>
          <h1 className="text-4xl font-bold mb-3">제품 카탈로그</h1>
          <p className="text-stone-600 text-sm">전문 파트너사가 제작한 최고 품질의 가방</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* 내부판매용 섹션 — 재고/Sold Out 강조 */}
        {hasInternal && (
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Tag size={24} className="text-amber-700" aria-hidden="true" />
              <div>
                <h2 className="text-xl font-bold text-stone-900">바로 구매 가능한 제품</h2>
                <p className="text-stone-500 text-sm">실시간 재고 연동 — 재고 소진 시 자동으로 SOLD OUT 표시</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {internalProducts.map(p => (
                <ProductCard key={p.id} product={p} showStock />
              ))}
            </div>
          </div>
        )}

        {/* 검색 & 필터 */}
        <div className="flex flex-wrap gap-3 mb-8 items-center">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="제품 검색..."
            className="border border-stone-200 rounded-full px-4 py-2 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
          />
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => {
              const Icon = CATEGORY_ICONS[cat];
              return (
              <button key={cat} onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  filter === cat
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-amber-300'
                }`}>
                {cat !== '전체' && Icon && <Icon size={15} className="mr-1 inline" aria-hidden="true" />}
                {cat}
              </button>
            )})}
          </div>
        </div>

        {/* 전체 제품 그리드 */}
        {loading ? (
          <div className="text-center py-24 text-stone-600 animate-pulse">제품 목록을 불러오는 중...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-stone-600">
            <Search size={36} className="mx-auto mb-4 text-stone-400" aria-hidden="true" />
            <p>검색 결과가 없습니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map(p => (
              <ProductCard key={p.id} product={p} showStock={p.category === '내부판매용'} />
            ))}
          </div>
        )}

        {/* OEM/ODM 안내 */}
        <div className="mt-20 bg-white text-stone-900 rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-bold mb-3">맞춤 제작이 필요하신가요?</h2>
          <p className="text-stone-600 mb-6">OEM/ODM 전문 제작으로 원하는 디자인과 수량으로 제작해 드립니다.</p>
          <Link to="/oem"
            className="inline-block bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-3 rounded-full transition">
            전문 제작 문의
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── 제품 카드 컴포넌트 ── */
function ProductCard({ product: p, showStock }: { product: Product; showStock?: boolean }) {
  const isSoldOut = p.sold_out === 1;
  const navigate = useNavigate();

  return (
    <Link to={`/product/${p.id}`}
      className={`bg-white rounded-2xl shadow-sm overflow-hidden group transition hover:shadow-lg block ${isSoldOut ? 'opacity-80' : ''}`}>

      {/* 이미지 영역 */}
      <div className="relative bg-stone-100 h-56 flex items-center justify-center overflow-hidden">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name}
            className={`w-full h-full object-cover transition group-hover:scale-105 ${isSoldOut ? 'grayscale' : ''}`} />
        ) : (
          <ShoppingBag size={48} className="text-stone-300" aria-label="제품 이미지 없음" />
        )}

        {/* SOLD OUT 오버레이 */}
        {isSoldOut && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-black text-white text-sm font-bold px-4 py-2 rounded-full tracking-widest">
              SOLD OUT
            </span>
          </div>
        )}

        {/* 재고 배지 (내부판매용 & 재고 있음) */}
        {showStock && !isSoldOut && (
          <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
            재고 {p.stock}개
          </div>
        )}

        {/* 카테고리 배지 */}
        {p.category === '내부판매용' && !isSoldOut && (
          <div className="absolute top-3 left-3 bg-amber-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
            즉시 구매
          </div>
        )}
      </div>

      {/* 정보 영역 */}
      <div className="p-4">
        <p className="text-xs text-stone-600 mb-1">{p.category}</p>
        <h3 className="font-semibold text-stone-800 mb-2 line-clamp-2">{p.name}</h3>
        <div className="flex items-center justify-between">
          <p className={`font-bold text-lg ${isSoldOut ? 'text-stone-600' : 'text-amber-600'}`}>
            {p.price.toLocaleString()}원
          </p>
          {isSoldOut ? (
            <span className="text-xs text-stone-600 font-semibold">품절</span>
          ) : p.category === '내부판매용' ? (
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/oem');
              }}
              className="text-xs bg-amber-500 hover:bg-amber-600 text-black font-bold px-3 py-1.5 rounded-full transition cursor-pointer"
            >
              구매 문의
            </span>
          ) : (
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/oem');
              }}
              className="text-xs border border-stone-300 hover:border-amber-400 hover:text-amber-600 text-stone-500 font-semibold px-3 py-1.5 rounded-full transition cursor-pointer"
            >
              문의
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
